import type { Vec2 } from '../../geo/types';
import type { WaterEdge } from '../../site/types';
import type { WaterAccess } from '../../site/waterAccess';
import { distancePointSegment } from '../../geo/polygon';

export interface BankOpening {
  readonly centre: Vec2;
  readonly halfWidth: number;
  /** Stair sides are exact vertical planes even where an earth bank's foot miters sideways. */
  readonly across?: Vec2;
}

/** A bank must give way to an outward stair's full width: an earth slope otherwise masks
 * the first two treads even though the walking query already follows their lower heights. */
export function stairBankOpening(
  edge: WaterEdge,
  flights: readonly WaterAccess[]
): BankOpening | null {
  const flight = flights.find(
    (access) =>
      distancePointSegment(access.origin, edge.segment) <= 0.3 &&
      Math.cos(access.heading) * edge.towardWater[0] +
        Math.sin(access.heading) * edge.towardWater[1] >
        0.9
  );
  if (!flight) return null;
  const { a, b } = edge.segment;
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const unit: Vec2 = [(b[0] - a[0]) / length, (b[1] - a[1]) / length];
  const along = (flight.origin[0] - a[0]) * unit[0] + (flight.origin[1] - a[1]) * unit[1];
  const across = Math.abs(-Math.sin(flight.heading) * unit[0] + Math.cos(flight.heading) * unit[1]);
  return {
    centre: [a[0] + along * unit[0], a[1] + along * unit[1]],
    halfWidth: flight.width / (2 * across),
    across: [-Math.sin(flight.heading), Math.cos(flight.heading)],
  };
}
